import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import VacancyCard from '../components/VacancyCard';
import vacanciesService from '../services/vacancies.service';
import { Vacancy, VacancyFilters } from '../types/VacancyTypes';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface BrowseVacanciesProps {
  darkMode: boolean;
}

const PAGE_SIZE = 20;

interface TagCounts {
  departments: Record<string, number>;
  seniorities: Record<string, number>;
  workStyles: Record<string, number>;
  companies: Record<string, number>;
}

const SENIORITY_ORDER = ['Intern', 'Junior', 'Mid', 'Senior', 'Lead', 'Manager', 'Director', 'VP', 'C-Level'];

function FilterSection({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="filter-section">
      <button className="filter-section-header" onClick={() => setOpen(!open)}>
        <span>{title}</span>
        {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
      </button>
      {open && <div className="filter-section-body">{children}</div>}
    </div>
  );
}

const BrowseVacancies: React.FC<BrowseVacanciesProps> = ({ darkMode }) => {
  const navigate = useNavigate();
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [tagCounts, setTagCounts] = useState<TagCounts>({ departments: {}, seniorities: {}, workStyles: {}, companies: {} });
  const [companySearch, setCompanySearch] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [filters, setFilters] = useState<VacancyFilters>({
    search: '',
    departments: [],
    seniorities: [],
    workStyles: [],
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    vacanciesService.getTagCounts().then(setTagCounts);
  }, []);

  const fetchVacancies = useCallback(async () => {
    setLoading(true);
    try {
      const result = await vacanciesService.getVacancies(filters, page, PAGE_SIZE);
      setVacancies(result.vacancies);
      setTotal(result.total);
    } catch {
      // handled by mock fallback
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchVacancies();
  }, [fetchVacancies]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setFilters(prev => ({ ...prev, search: searchInput }));
  };

  const handleCardClick = (id: string) => {
    navigate(`/vacancies/${id}`);
  };

  // Toggle helpers
  const toggleArrayFilter = (key: 'departments' | 'seniorities' | 'workStyles', value: string) => {
    setPage(1);
    setFilters(prev => {
      const arr = prev[key] || [];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value],
      };
    });
  };

  const setCompanyFilter = (company: string | undefined) => {
    setPage(1);
    setFilters(prev => ({ ...prev, company }));
  };

  // Active filter chips
  const activeChips = useMemo(() => {
    const chips: { label: string; onRemove: () => void }[] = [];
    if (filters.search) {
      chips.push({ label: `"${filters.search}"`, onRemove: () => { setSearchInput(''); setFilters(prev => ({ ...prev, search: '' })); } });
    }
    if (filters.company) {
      chips.push({ label: filters.company, onRemove: () => setCompanyFilter(undefined) });
    }
    for (const d of filters.departments || []) {
      chips.push({ label: d, onRemove: () => toggleArrayFilter('departments', d) });
    }
    for (const s of filters.seniorities || []) {
      chips.push({ label: s, onRemove: () => toggleArrayFilter('seniorities', s) });
    }
    for (const w of filters.workStyles || []) {
      chips.push({ label: w, onRemove: () => toggleArrayFilter('workStyles', w) });
    }
    if (filters.minBounty != null) {
      chips.push({ label: `Min $${filters.minBounty}`, onRemove: () => setFilters(prev => ({ ...prev, minBounty: undefined })) });
    }
    if (filters.maxBounty != null) {
      chips.push({ label: `Max $${filters.maxBounty}`, onRemove: () => setFilters(prev => ({ ...prev, maxBounty: undefined })) });
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const clearAllFilters = () => {
    setSearchInput('');
    setPage(1);
    setFilters({
      search: '',
      departments: [],
      seniorities: [],
      workStyles: [],
      sortBy: 'created_at',
      sortOrder: 'desc',
    });
  };

  // Sorted facets
  const sortedDepartments = Object.entries(tagCounts.departments).sort((a, b) => b[1] - a[1]);
  const sortedSeniorities = Object.entries(tagCounts.seniorities).sort(
    (a, b) => SENIORITY_ORDER.indexOf(a[0]) - SENIORITY_ORDER.indexOf(b[0])
  );
  const sortedWorkStyles = Object.entries(tagCounts.workStyles).sort((a, b) => b[1] - a[1]);
  const filteredCompanies = Object.entries(tagCounts.companies)
    .filter(([name]) => !companySearch || name.toLowerCase().includes(companySearch.toLowerCase()))
    .sort((a, b) => b[1] - a[1]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const sidebarContent = (
    <>
      {/* Search */}
      <div className="sidebar-search">
        <form onSubmit={handleSearch}>
          <div className="search-bar">
            <SearchIcon sx={{ fontSize: 18, color: '#888' }} />
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn-search">Go</button>
          </div>
        </form>
      </div>

      {/* Company */}
      <FilterSection title="Company">
        <input
          type="text"
          className="filter-search-input"
          placeholder="Search companies..."
          value={companySearch}
          onChange={e => setCompanySearch(e.target.value)}
        />
        <div className="filter-checkbox-list">
          {filteredCompanies.slice(0, 20).map(([name, count]) => (
            <label key={name} className="filter-checkbox-item">
              <input
                type="checkbox"
                checked={filters.company === name}
                onChange={() => setCompanyFilter(filters.company === name ? undefined : name)}
              />
              <span className="filter-checkbox-label">{name}</span>
              <span className="filter-checkbox-count">{count}</span>
            </label>
          ))}
          {filteredCompanies.length > 20 && (
            <span className="filter-more-hint">{filteredCompanies.length - 20} more...</span>
          )}
        </div>
      </FilterSection>

      {/* Department */}
      <FilterSection title="Department">
        <div className="filter-checkbox-list">
          {sortedDepartments.map(([name, count]) => (
            <label key={name} className="filter-checkbox-item">
              <input
                type="checkbox"
                checked={(filters.departments || []).includes(name)}
                onChange={() => toggleArrayFilter('departments', name)}
              />
              <span className="filter-checkbox-label">{name}</span>
              <span className="filter-checkbox-count">{count}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Seniority */}
      <FilterSection title="Seniority">
        <div className="filter-checkbox-list">
          {sortedSeniorities.map(([name, count]) => (
            <label key={name} className="filter-checkbox-item">
              <input
                type="checkbox"
                checked={(filters.seniorities || []).includes(name)}
                onChange={() => toggleArrayFilter('seniorities', name)}
              />
              <span className="filter-checkbox-label">{name}</span>
              <span className="filter-checkbox-count">{count}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Work Style */}
      <FilterSection title="Work Style">
        <div className="filter-checkbox-list">
          {sortedWorkStyles.map(([name, count]) => (
            <label key={name} className="filter-checkbox-item">
              <input
                type="checkbox"
                checked={(filters.workStyles || []).includes(name)}
                onChange={() => toggleArrayFilter('workStyles', name)}
              />
              <span className="filter-checkbox-label">{name}</span>
              <span className="filter-checkbox-count">{count}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Bounty Range */}
      <FilterSection title="Bounty Range" defaultOpen={false}>
        <div className="filter-bounty-inputs">
          <input
            type="number"
            placeholder="Min"
            value={filters.minBounty ?? ''}
            onChange={e => {
              setPage(1);
              setFilters(prev => ({ ...prev, minBounty: e.target.value ? Number(e.target.value) : undefined }));
            }}
          />
          <span>-</span>
          <input
            type="number"
            placeholder="Max"
            value={filters.maxBounty ?? ''}
            onChange={e => {
              setPage(1);
              setFilters(prev => ({ ...prev, maxBounty: e.target.value ? Number(e.target.value) : undefined }));
            }}
          />
        </div>
      </FilterSection>

      {/* Sort */}
      <FilterSection title="Sort By" defaultOpen={false}>
        <select
          className="filter-sort-select"
          value={`${filters.sortBy}_${filters.sortOrder}`}
          onChange={e => {
            const [sortBy, sortOrder] = e.target.value.split('_') as [VacancyFilters['sortBy'], VacancyFilters['sortOrder']];
            setFilters(prev => ({ ...prev, sortBy, sortOrder }));
          }}
        >
          <option value="created_at_desc">Newest First</option>
          <option value="created_at_asc">Oldest First</option>
          <option value="bounty_amount_desc">Highest Bounty</option>
          <option value="bounty_amount_asc">Lowest Bounty</option>
          <option value="title_asc">Title A-Z</option>
          <option value="title_desc">Title Z-A</option>
        </select>
      </FilterSection>

      {activeChips.length > 0 && (
        <button className="clear-all-filters" onClick={clearAllFilters}>
          Clear All Filters
        </button>
      )}
    </>
  );

  return (
    <div className={`page-container browse-page ${darkMode ? 'dark' : ''}`}>
      <div className="browse-header">
        <h1>Browse Vacancies</h1>
        <p className="browse-subtitle">{total.toLocaleString()} open positions from top companies</p>
      </div>

      {/* Mobile filter toggle */}
      <button
        className="btn-filter-toggle mobile-only"
        onClick={() => setMobileSidebarOpen(true)}
      >
        <TuneIcon sx={{ fontSize: 18 }} />
        Filters
      </button>

      <div className="browse-layout">
        {/* Sidebar - desktop */}
        <aside className="browse-sidebar desktop-only">
          {sidebarContent}
        </aside>

        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <div className="sidebar-overlay" onClick={() => setMobileSidebarOpen(false)}>
            <aside className="browse-sidebar mobile-drawer" onClick={e => e.stopPropagation()}>
              <div className="mobile-drawer-header">
                <h3>Filters</h3>
                <button className="modal-close-btn" onClick={() => setMobileSidebarOpen(false)}>
                  <CloseIcon sx={{ fontSize: 20 }} />
                </button>
              </div>
              {sidebarContent}
            </aside>
          </div>
        )}

        {/* Content area */}
        <div className="browse-content">
          {/* Active filter chips */}
          {activeChips.length > 0 && (
            <div className="active-filter-chips">
              {activeChips.map((chip, i) => (
                <span key={i} className="filter-chip">
                  {chip.label}
                  <button onClick={chip.onRemove} className="filter-chip-remove">
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </button>
                </span>
              ))}
              <button className="filter-chips-clear" onClick={clearAllFilters}>Clear all</button>
            </div>
          )}

          {/* Results count */}
          <div className="browse-results-count">
            {loading ? 'Loading...' : `${total.toLocaleString()} position${total !== 1 ? 's' : ''}${activeChips.length > 0 ? ' matching filters' : ''}`}
          </div>

          {loading ? (
            <div className="loading-state">Loading vacancies...</div>
          ) : vacancies.length === 0 ? (
            <div className="empty-state">
              <h3>No vacancies found</h3>
              <p>Try adjusting your search or filters.</p>
            </div>
          ) : (
            <>
              <div className="vacancy-grid">
                {vacancies.map(v => (
                  <VacancyCard key={v.id} vacancy={v} onClick={handleCardClick} darkMode={darkMode} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn-page"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} /> Prev
                  </button>
                  <span className="page-info">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn-page"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowseVacancies;
